import { createContext, useContext, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

/**
 * Portal target for `PageTopNav`. The shell renders a slot above the
 * scroll container; routes render their nav into that slot via
 * `<PageTopNavOutlet>`. This keeps the nav OUTSIDE the page scroll so
 * it never has to fight sticky-positioning quirks (grid/flex containing
 * blocks, scrollport padding, stacking contexts) — it just lives in the
 * shell, always pinned, and content scrolls beneath it inside `.content`.
 */
const TopNavSlotContext = createContext<HTMLElement | null>(null)

export function TopNavSlotProvider({
  slot,
  children,
}: {
  slot: HTMLElement | null
  children: ReactNode
}) {
  return <TopNavSlotContext.Provider value={slot}>{children}</TopNavSlotContext.Provider>
}

/**
 * Helper used by `AppShell` to obtain the ref-callback for the slot.
 * Returns `[slot, setSlot]` shaped like a state tuple so the shell can
 * pass `setSlot` to a `ref={...}` on the slot div.
 */
export function useTopNavSlotRef(): [HTMLElement | null, (el: HTMLElement | null) => void] {
  return useState<HTMLElement | null>(null)
}

/**
 * Renders its children into the shell's top-nav slot. Routes use this
 * to declare their `PageTopNav` without owning where it physically
 * mounts in the tree.
 */
export function PageTopNavOutlet({ children }: { children: ReactNode }) {
  const slot = useContext(TopNavSlotContext)
  if (!slot) return null
  return createPortal(children, slot)
}
